import axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { getErrorCode } from '@/utils/errors';

// Base URL without /api suffix.
// Default to empty string → API calls go to relative URLs (/api/...) which
// are always same-origin. This avoids CORS preflight friction on iOS Safari
// (which intermittently shows "NetworkError" for cross-origin POSTs even
// when CORS headers are correct) and keeps the app working on apex/www/any
// custom domain without rebuilding the bundle.
//
// Override only when frontend and backend are on different hosts (e.g. a
// dedicated api.* subdomain). Use ?? not || so an explicit empty string
// from .env.production is preserved as relative.
//
// Dev: vite.config.ts proxies `/api` → http://localhost:8000 automatically.
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

// Full API URL with /api prefix (e.g., '/api' relative, or 'https://api.host/api' absolute)
export const API_URL = `${API_BASE_URL}/api`;

// Helper function for constructing API URLs
export function getApiUrl(path: string): string {
  return `${API_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

// Helper function for constructing media/video URLs (without /api prefix)
export function getMediaUrl(path: string): string {
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Refresh-retry state — prevents concurrent refresh storms
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function drainQueue(token: string | null, err: unknown) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(err);
    }
  });
  pendingQueue = [];
}

function redirectToLogin() {
  localStorage.removeItem('authToken');
  // The refresh token now lives in an httpOnly cookie — the browser will send
  // it on the next /api/auth/logout call. We don't clear it here because
  // localStorage no longer holds it.
  window.location.href = '/login';
}

// Response interceptor — handle password_change_required (403) before the 401 refresh chain.
//
// When the backend returns 403 with error code "password_change_required" the user must
// complete a forced password change before accessing any other endpoint.  This interceptor
// redirects them immediately to /auth/change-password so the experience is seamless.
//
// IMPORTANT: only intercept the one specific code — all other 403 errors are passed through
// unchanged so unrelated permission-denied cases are not affected.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && getErrorCode(error) === 'password_change_required') {
      window.location.href = '/auth/change-password';
    }
    return Promise.reject(error);
  }
);

// Response interceptor — attempt token refresh on 401 before redirecting
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    // Only attempt refresh for 401s that haven't already been retried
    if (error.response?.status !== 401 || originalRequest._retried) {
      return Promise.reject(error);
    }

    // The refresh token now lives in an httpOnly cookie — we don't check
    // localStorage for it. Just attempt the refresh; if the cookie is absent
    // or expired the backend will return 401 and we'll redirect to login.

    // If a refresh is already in flight, queue this request until it resolves
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((newToken) => {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      });
    }

    // This request will drive the refresh
    originalRequest._retried = true;
    isRefreshing = true;

    try {
      // Send empty body; the httpOnly cookie is attached automatically by the
      // browser because withCredentials=true. The backend reads the cookie and
      // issues a new access token.
      const response = await axios.post<{ access_token: string }>(
        `${API_URL}/auth/refresh`,
        {},
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );

      const { access_token } = response.data;
      localStorage.setItem('authToken', access_token);
      // The backend also rotates the refresh token cookie on each refresh —
      // no localStorage write needed.

      // Unblock all queued requests with the new token
      drainQueue(access_token, null);

      // Retry the original request
      originalRequest.headers.Authorization = `Bearer ${access_token}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh failed (e.g. cookie absent/expired) — redirect to login
      drainQueue(null, refreshError);
      redirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
